import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { subscribeToPreferences, broadcastPreferenceUpdate } from '../lib/preferencesChannel';

const ThemeContext = createContext();

// Key used both for localStorage and for the cross-tab broadcast message.
const THEME_KEY = 'yt-theme';
// Set only when the USER explicitly picks a theme (toggle / Settings).
// Absent → we apply the app default (dark), even if an auto-written
// yt-theme value from the old light-default era is still present.
const THEME_CHOSEN_KEY = 'yt-theme-chosen';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      if (localStorage.getItem(THEME_CHOSEN_KEY) === '1') {
        return localStorage.getItem(THEME_KEY) || 'dark';
      }
      return 'dark';
    }
    return 'dark';
  });

  // Setter that ALSO broadcasts to other tabs. Anything that changes theme
  // (toggle button, Settings page, etc.) should call this — never setState
  // directly — so cross-tab sync is guaranteed.
  const setTheme = React.useCallback((next) => {
    localStorage.setItem(THEME_CHOSEN_KEY, '1');
    setThemeState(next);
    broadcastPreferenceUpdate(THEME_KEY, next);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Cross-tab receiver: apply theme changes broadcast by other tabs. We use
  // the plain setter here (not `setTheme`) so we don't echo the message
  // back and cause a loop.
  useEffect(() => {
    return subscribeToPreferences(({ key, value }) => {
      if (key !== THEME_KEY) return;
      if (value !== 'dark' && value !== 'light') return;
      setThemeState(value);
    });
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
  }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
